// BaseFlow Local Client - Main client class

import { BaseFlowLocalClientOptions, QueryBuilder, AuthEvent, BaseFlowResponse, StorageUploadOptions, StorageFile } from './types';
import { LocalQueryBuilderImpl } from './query-builder';

/**
 * The main client for interacting with a BaseFlow Local backend.
 *
 * @example
 * ```ts
 * const client = createClient({
 *   url: 'http://localhost:5555'
 * });
 *
 * const { data, error } = await client.from('users').select('*');
 * ```
 */
export class BaseFlowLocalClient {
  /** The URL of the BaseFlow Local backend. */
  private url: string;
  /** Additional headers to send with each request. */
  private headers: Record<string, string>;
  /** The underlying fetch implementation. */
  private fetch: typeof fetch;
  /** The authentication API. */
  public auth: AuthAPI;
  /** The storage API. */
  public storage: StorageAPI;

  /**
   * Creates a new BaseFlowLocalClient instance.
   *
   * @param options The options for the client.
   */
  constructor(options: BaseFlowLocalClientOptions = {}) {
    this.url = (options.url || 'http://localhost:5555').replace(/\/$/, ''); // Remove trailing slash
    this.headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (options.token) {
      this.headers['Authorization'] = `Bearer ${options.token}`;
    }

    if (options.apiKey) {
      this.headers['x-api-key'] = options.apiKey;
    }
    
    // Use provided fetch or globalThis.fetch or cross-fetch
    if (options.fetch) {
      this.fetch = options.fetch;
    } else if (typeof globalThis.fetch === 'function') {
      this.fetch = (...args) => globalThis.fetch(...args);
    } else {
      this.fetch = require('cross-fetch');
    }
    
    // Initialize API modules
    this.storage = new StorageAPI(this);
    this.auth = new AuthAPI(this);
  }

  /**
   * Creates a query builder for a specific table.
   *
   * @param table The name of the table to query.
   * @returns A query builder for the specified table.
   */
  from<T = any>(table: string): QueryBuilder<T> {
    return new LocalQueryBuilderImpl<T>(this, table);
  }

  /**
   * Makes a request to the BaseFlow Local backend.
   *
   * @param method The HTTP method to use.
   * @param path The path to request.
   * @param options Additional options for the request.
   * @returns The response from the backend.
   */
  async request(
    method: string,
    path: string,
    options: {
      body?: any;
      headers?: Record<string, string>;
      params?: Record<string, any>;
    } = {}
  ): Promise<BaseFlowResponse> {
    const url = new URL(`${this.url}${path}`);
    
    // Add query parameters
    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const requestHeaders = {
      ...this.headers,
      ...options.headers
    };

    const requestOptions: RequestInit = {
      method,
      headers: requestHeaders
    };

    if (options.body && method !== 'GET' && method !== 'HEAD') {
      requestOptions.body = typeof options.body === 'string' 
        ? options.body 
        : JSON.stringify(options.body);
    }

    try {
      const response = await this.fetch(url.toString(), requestOptions);
      
      let data = null;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        return {
          data: null,
          error: {
            message: data?.error || data?.message || `HTTP ${response.status}`,
            code: data?.code || 'HTTP_ERROR',
            details: data
          },
          status: response.status,
          statusText: response.statusText
        };
      }

      return {
        data,
        error: null,
        status: response.status,
        statusText: response.statusText
      };
    } catch (error) {
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Network error',
          code: 'NETWORK_ERROR',
          details: error
        },
        status: 0,
        statusText: 'Network Error'
      };
    }
  }

  /**
   * Set authentication token
   */
  setAuth(token: string | null) {
    if (token) {
      this.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.headers['Authorization'];
    }
  }

  /**
   * Call a remote procedure/function
   */
  async rpc(functionName: string, params: any = {}) {
    return this.request('POST', `/api/rpc/${functionName}`, {
      body: params
    });
  }

  /**
   * Get current schema
   */
  async getSchema() {
    return this.request('GET', '/api/schema');
  }

  /**
   * List all tables
   */
  async listTables() {
    const response = await this.request('GET', '/api/tables');
    // Server returns { success: true, data: [...], count: ... }
    // Unwrap to { data: { tables: [...] }, error: null }
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      return {
        data: {
          tables: response.data.data || []
        },
        error: response.error,
        status: response.status,
        statusText: response.statusText
      };
    }
    return response;
  }
}

/**
 * Storage API for file operations
 */
class StorageAPI {
  constructor(private client: BaseFlowLocalClient) {}

  /**
   * Upload a file
   */
  async upload(file: File | Buffer | Blob, options: StorageUploadOptions = {}): Promise<BaseFlowResponse<StorageFile>> {
    try {
      const formData = new FormData();
      
      // Handle different file types
      if (file instanceof File) {
        formData.append('file', file);
      } else if (Buffer.isBuffer(file)) {
        // Convert Buffer to Blob for FormData
        const blob = new Blob([file as any]);
        formData.append('file', blob, options.filename || 'file');
      } else {
        formData.append('file', file);
      }
      
      if (options.folder) {
        formData.append('folder', options.folder);
      }
      
      if (options.isPublic !== undefined) {
        formData.append('isPublic', String(options.isPublic));
      }

      const url = new URL(`${this.client['url']}/api/storage/upload`);
      
      const requestOptions: RequestInit = {
        method: 'POST',
        headers: {
          ...(this.client['headers']['Authorization'] && {
            'Authorization': this.client['headers']['Authorization']
          })
        },
        body: formData
      };

      const response = await this.client['fetch'](url.toString(), requestOptions);
      
      let data = null;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      }

      if (!response.ok) {
        return {
          data: null,
          error: {
            message: data?.error || `Upload failed: HTTP ${response.status}`,
            code: 'UPLOAD_ERROR',
            details: data
          }
        };
      }

      return {
        data: data,
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Upload failed',
          code: 'NETWORK_ERROR',
          details: error
        }
      };
    }
  }

  /**
   * List files
   */
  async list(folder?: string): Promise<BaseFlowResponse<{ files: StorageFile[]; count: number }>> {
    return this.client.request('GET', '/api/storage', {
      params: folder ? { folder } : {}
    });
  }

  /**
   * Get file URL
   */
  getUrl(filePath: string): string {
    return `${this.client['url']}/api/storage/${filePath}`;
  }

  /**
   * Download file
   */
  async download(filePath: string) {
    return this.client.request('GET', `/api/storage/${filePath}`);
  }

  /**
   * Delete file
   */
  async delete(fileId: number) {
    return this.client.request('DELETE', `/api/storage/${fileId}`);
  }
}

/**
 * Auth API for authentication operations
 */
class AuthAPI {
  private authToken: string | null = null;
  private user: any = null;
  private listeners: Array<(event: AuthEvent) => void> = [];

  constructor(private client: BaseFlowLocalClient) {}

  /**
   * Register a new user
   */
  async signUp(credentials: { email: string; password: string; name?: string }) {
    const response = await this.client.request('POST', '/api/auth/register', {
      body: credentials
    });

    if (response.data && response.data.user) {
      this.user = response.data.user;
      if (response.data.token) {
        this.authToken = response.data.token;
        this.client.setAuth(this.authToken);
        this.notifyListeners({ type: 'SIGNED_IN', user: this.user });
      }
    }

    return response;
  }

  /**
   * Sign in with email and password
   */
  async signInWithPassword(credentials: { email: string; password: string }) {
    const response = await this.client.request('POST', '/api/auth/login', {
      body: credentials
    });

    if (response.data && response.data.user) {
      this.user = response.data.user;
      if (response.data.token) {
        this.authToken = response.data.token;
        this.client.setAuth(this.authToken);
        this.notifyListeners({ type: 'SIGNED_IN', user: this.user });
      }
    }

    return response;
  }

  /**
   * Sign out the current user
   */
  async signOut() {
    const response = await this.client.request('POST', '/api/auth/logout');

    this.authToken = null;
    this.user = null;
    this.client.setAuth(null);
    this.notifyListeners({ type: 'SIGNED_OUT' });

    return response;
  }

  /**
   * Get the current user
   */
  async getUser() {
    if (!this.authToken) {
      return { data: { user: null }, error: null };
    }

    const response = await this.client.request('GET', '/api/auth/me');

    if (response.data && response.data.user) {
      this.user = response.data.user;
    }

    return response;
  }

  /**
   * Get the current session
   */
  getSession() {
    return {
      token: this.authToken,
      user: this.user
    };
  }

  /**
   * Set the auth token manually
   */
  setSession(session: { token: string; user?: any }) {
    this.authToken = session.token;
    this.user = session.user || null;
    this.client.setAuth(this.authToken);
    
    if (this.user) {
      this.notifyListeners({ type: 'SIGNED_IN', user: this.user });
    }
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(callback: (event: AuthEvent) => void) {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of auth events
   */
  private notifyListeners(event: AuthEvent) {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Auth listener error:', error);
      }
    });
  }

  // Legacy methods for backward compatibility
  async register(email: string, password: string, name?: string) {
    return this.signUp({ email, password, name });
  }

  async login(email: string, password: string) {
    return this.signInWithPassword({ email, password });
  }

  async logout() {
    return this.signOut();
  }
}

/**
 * Create a new BaseFlow Local client instance
 */
export function createClient(options: BaseFlowLocalClientOptions = {}): BaseFlowLocalClient {
  return new BaseFlowLocalClient(options);
}
